// Statements panel — shows the participant's auto-generated monthly /
// quarterly statements and lets them download or generate new ones.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  FileText, Download, Plus, Loader2, Calendar,
} from 'lucide-react';
import { statementsAPI } from '@/lib/api';

const fmtBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1_048_576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1_048_576).toFixed(1)} MB`;
};

const PERIOD_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  snapshot: 'Snapshot',
};

export const StatementsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [period, setPeriod] = useState('monthly');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [index, setIndex] = useState(now.getMonth() + 1);

  const load = () => {
    setLoading(true);
    statementsAPI
      .list()
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await statementsAPI.generate(period, parseInt(year, 10), parseInt(index, 10));
      toast.success('Statement generated');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (id, label) => {
    const link = document.createElement('a');
    link.href = statementsAPI.downloadUrl(id);
    link.setAttribute('download', `UniFed_Statement_${label}.pdf`);
    const token = localStorage.getItem('access_token');
    // Use a fetch with auth header, then create object URL (can't inject headers on <a>)
    fetch(link.href, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Download failed'));
  };

  const input =
    'h-9 px-2 bg-white border border-[#D1D5DB] rounded text-xs text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A]';

  const indexLabel = period === 'quarterly' ? 'Quarter' : 'Month';
  const indexMax = period === 'quarterly' ? 4 : 12;

  return (
    <div className="panel p-5" data-testid="statements-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <FileText size={15} className="text-[#1B2A4A]" />
          <h2 className="text-sm font-semibold text-[#1B2A4A]">Account Statements</h2>
        </div>
        <span className="text-[10px] text-[#9CA3AF]">
          Auto-generated PDF statements · keep for your records
        </span>
      </div>

      {/* Generate controls */}
      <div
        className="flex items-end gap-2 flex-wrap bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3 mb-4"
        data-testid="statements-generate-row"
      >
        <div>
          <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">
            Period
          </label>
          <select
            data-testid="statements-period-select"
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
              setIndex(1);
            }}
            className={input}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">
            Year
          </label>
          <input
            type="number"
            min={2020}
            max={now.getFullYear()}
            data-testid="statements-year-input"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className={input + ' w-24 font-mono'}
          />
        </div>
        <div>
          <label className="block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1">
            {indexLabel}
          </label>
          <input
            type="number"
            min={1}
            max={indexMax}
            data-testid="statements-index-input"
            value={index}
            onChange={(e) => setIndex(e.target.value)}
            className={input + ' w-20 font-mono'}
          />
        </div>
        <button
          data-testid="statements-generate-btn"
          onClick={handleGenerate}
          disabled={generating}
          className="inline-flex items-center gap-1 h-9 px-4 rounded text-xs font-semibold text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-50"
        >
          {generating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          Generate
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
          <Loader2 size={12} className="animate-spin" /> Loading statements…
        </div>
      ) : items.length === 0 ? (
        <p className="text-[11px] text-[#9CA3AF] italic" data-testid="statements-empty">
          No statements yet. Use the controls above to generate your first statement.
        </p>
      ) : (
        <div className="divide-y divide-[#F0F1F3] border border-[#E2E5E9] rounded" data-testid="statements-list">
          {items.map((s) => (
            <div
              key={s.id}
              data-testid={`statement-row-${s.id}`}
              className="flex items-center justify-between gap-3 px-3 py-2.5 text-[12px]"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Calendar size={12} className="text-[#6B7280] flex-shrink-0" />
                <span className="font-semibold text-[#1B2A4A] font-mono">{s.label}</span>
                <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                  {PERIOD_LABELS[s.period] || s.period}
                </span>
              </div>
              <span className="text-[10px] text-[#9CA3AF] font-mono">
                {fmtBytes(s.bytes_size)}
              </span>
              <button
                data-testid={`statement-download-${s.id}`}
                onClick={() => handleDownload(s.id, s.label)}
                className="inline-flex items-center gap-1 h-7 px-2.5 rounded text-[11px] font-semibold text-[#1B2A4A] border border-[#D1D5DB] hover:border-[#1B2A4A] hover:bg-gray-50"
              >
                <Download size={11} />
                Download
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatementsPanel;
